import type { ReviewFormField } from './reviewFormModel'

/**
 * Ошибка Showcase API отзывов: `ProblemDetails` с кодом в `type` (`ReviewRuleException`)
 * или 422 `VALIDATION_ERROR` с ошибками по полям.
 */
export type ReviewApiErrorLike = {
  type?: string
  title?: string
  detail?: string
  status?: number
  errors?: Record<string, string[] | undefined>
}

const MESSAGES: Record<string, string> = {
  review_auth_required: 'Войдите, чтобы оставить отзыв',
  review_already_exists: 'Вы уже оставили отзыв на этот товар. Изменить его можно в разделе «Мои отзывы»',
  review_purchase_required: 'Оставить отзыв можно после покупки товара',
  review_product_not_found: 'Товар не найден на этой витрине',
  review_not_found: 'Отзыв не найден — возможно, он уже удалён',
  review_forbidden: 'Это не ваш отзыв',
}

const FALLBACK = 'Не удалось отправить отзыв. Попробуйте ещё раз'

const FORM_FIELDS: readonly string[] = ['rating', 'pros', 'cons', 'text', 'resourceIds'] satisfies ReviewFormField[]

/** Ошибки сервера, которые форма показывает под своими полями: первая для каждого поля. */
export function reviewApiFieldErrors(error: ReviewApiErrorLike | null | undefined): Partial<Record<ReviewFormField, string>> {
  const result: Partial<Record<ReviewFormField, string>> = {}
  for (const [key, messages] of Object.entries(error?.errors ?? {})) {
    if (FORM_FIELDS.includes(key) && messages?.[0]) result[key as ReviewFormField] = messages[0]
  }
  return result
}

/**
 * Общее сообщение над кнопками формы. Для ошибок полей формы его нет — они показаны под полями;
 * ошибки прочих полей собираются в одну строку.
 */
export function reviewApiErrorMessage(error: ReviewApiErrorLike | null | undefined): string | undefined {
  if (!error) return undefined
  if (error.type && MESSAGES[error.type]) return MESSAGES[error.type]

  if (error.errors) {
    const other = Object.entries(error.errors)
      .filter(([key]) => !FORM_FIELDS.includes(key))
      .flatMap(([, messages]) => messages ?? [])
    return other.length ? other.join('. ') : undefined
  }

  return error.detail || error.title || FALLBACK
}

/** Действие рядом с ошибкой: на уже оставленный отзыв — переход в «Мои отзывы», где его можно изменить. */
export type ReviewApiErrorAction = 'my-reviews'

export function reviewApiErrorAction(error: ReviewApiErrorLike | null | undefined): ReviewApiErrorAction | undefined {
  return error?.type === 'review_already_exists' ? 'my-reviews' : undefined
}
